import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tabs, Anchor } from 'antd';
import { 
  SafetyCertificateOutlined,
  FileProtectOutlined,
  ThunderboltOutlined,
  ForkOutlined,
  AimOutlined,
  DeploymentUnitOutlined
} from '@ant-design/icons';
import './KnowledgePage.less';
import { useI18n } from '../i18n/i18n';
// 集中来源：保护类型与名称映射
import { PROTECTION_TYPES } from '../utils/protectionTypes';
import { getProtectionTypeName } from '../utils/exportUtils';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const KnowledgePage = () => {
  const [activeKey, setActiveKey] = useState('1');
  const { t } = useI18n();


  // 统一图标与简要文案
  const ICON_MAP = {
    overcurrent: ThunderboltOutlined,
    distance: AimOutlined,
    differential: DeploymentUnitOutlined,
    directional: ForkOutlined,
    zero_sequence: ThunderboltOutlined,
    longitudinal: SafetyCertificateOutlined
  };
  const TYPE_DESC = {
    overcurrent: '按照反时限或定时限特性整定，适用于配电线路与备用保护',
    distance: '基于阻抗测量分区段（Z1/Z2/Z3），常用于中长距离输电线路',
    differential: '通过两端电流矢量差判断内部故障，适用于变压器/母线',
    directional: '以功率或阻抗相位判断故障方向，作为选择性判据',
    zero_sequence: '检测接地故障的零序电流/电压/功率，常与配电系统接地方式相关',
    longitudinal: '纵联差动或纵联保护，依赖通道比对两端信息，动作快速且选择性好'
  };

  const CONTENT_META = {
    overcurrent: (
      <>
        <Title level={3}>工作原理</Title>
        <Paragraph>
          过电流保护根据被保护对象的电流大小进行动作，一般采用反时限或定时限特性。通过比较相电流与整定值，超过阈值时延时或瞬时跳闸。反时限曲线可采用IEC、IEEE或用户自定义曲线。
        </Paragraph>
        <Title level={3}>应用场景</Title>
        <Paragraph>
          配电线路主保护、备用保护；电动机保护；作为输电线路的后备保护。
        </Paragraph>
        <Title level={3}>技术要点</Title>
        <Paragraph>
          整定需考虑最小短路电流、负荷电流、选择性配合与灵敏度；对零序分量的使用需结合接地方式（如中性点不接地、经消弧线圈接地等）。
        </Paragraph>
      </>
    ),
    distance: (
      <>
        <Title level={3}>工作原理</Title>
        <Paragraph>
          距离保护通过测量线路阻抗判断故障距离，采用分区段整定（Z1/Z2/Z3）与方向判据。常配合高抗饱和CT与PT，受负序分量与故障电阻影响。
        </Paragraph>
        <Title level={3}>应用场景</Title>
        <Paragraph>
          中长距离输电线路主保护，与纵联保护配合；短线路需注意故障电阻影响导致灵敏度下降。
        </Paragraph>
        <Title level={3}>技术要点</Title>
        <Paragraph>
          需进行阻抗图形（Mho/Quadrilateral）选择与区段定值校核；考虑并联线路、串补、弱电源等复杂工况。
        </Paragraph>
      </>
    ),
    differential: (
      <>
        <Title level={3}>工作原理</Title>
        <Paragraph>
          差动保护比较被保护对象两端电流矢量差，内部故障电流差显著增大而动作。采用分段制动系数与二次谐波制动来抑制CT饱和与励磁涌流的影响。
        </Paragraph>
        <Title level={3}>应用场景</Title>
        <Paragraph>
          变压器、发电机、母线等重要设备的主保护。
        </Paragraph>
        <Title level={3}>技术要点</Title>
        <Paragraph>
          需按变压器接线与变比进行相量平衡，考虑CT误差与涌流判据；母线差动需配合开关状态逻辑与分区选择。
        </Paragraph>
      </>
    ),
    directional: (
      <>
        <Title level={3}>工作原理</Title>
        <Paragraph>
          方向保护以功率方向、阻抗相位或序分量相位关系作为方向判据，识别故障是否在被保护区间的前向或后向。
        </Paragraph>
        <Title level={3}>应用场景</Title>
        <Paragraph>
          环网、双电源、并联线路等需要方向判据以保证选择性的场景。
        </Paragraph>
        <Title level={3}>技术要点</Title>
        <Paragraph>
          需选择合适的方向元素（有功功率、阻抗角、负序功率等），并与过电流或距离保护配合。
        </Paragraph>
      </>
    ),
    zero_sequence: (
      <>
        <Title level={3}>工作原理</Title>
        <Paragraph>
          零序保护通过测量零序电流、零序电压或零序功率来识别接地故障。不同接地方式下零序分量差异较大，需相应整定策略。
        </Paragraph>
        <Title level={3}>应用场景</Title>
        <Paragraph>
          配电网络与中压系统的接地故障保护，作为线路保护的补充与选择性判据。
        </Paragraph>
        <Title level={3}>技术要点</Title>
        <Paragraph>
          结合中性点接地方式（不接地、经电阻/电抗接地、消弧线圈接地）选择零序量与整定；考虑耦合与互感影响。
        </Paragraph>
      </>
    ),
    longitudinal: (
      <>
        <Title level={3}>工作原理</Title>
        <Paragraph>
          纵联保护通过通信通道比对线路两端量测（电流/阻抗/相位），实现对内部故障的快速选择性识别。常见方案包括纵联差动、方向比较、相量比较等。
        </Paragraph>
        <Title level={3}>应用场景</Title>
        <Paragraph>
          中长距离输电线路的主保护，与距离保护协同；在弱电源、环网等复杂系统中提升选择性与速度。
        </Paragraph>
        <Title level={3}>技术要点</Title>
        <Paragraph>
          依赖高可靠通信通道；需进行通道延迟、比对算法与失信策略的设计；与距离与后备保护配合整定。
        </Paragraph>
      </>
    )
  };

  const tabs = PROTECTION_TYPES.map((value, idx) => {
    const IconComp = ICON_MAP[value] || SafetyCertificateOutlined;
    return {
      key: String(idx + 1),
      title: getProtectionTypeName(value, { t }),
      description: TYPE_DESC[value] || '',
      icon: <IconComp />,
      content: CONTENT_META[value]
    };
  });

  return (
    <div className="knowledge-page">
      <div className="header">
        <Title level={2}>继电保护知识库</Title>
        <Paragraph>
          汇集常见保护类型的工作原理、应用场景与整定要点，帮助工程师系统化掌握保护技术。
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={18}>
          <Card className="content-card" variant="borderless">
            <Tabs activeKey={activeKey} onChange={setActiveKey}>
              {tabs.map(tab => (
                <TabPane 
                  tab={
                    <span>
                      {tab.icon} {tab.title}
                    </span>
                  } 
                  key={tab.key}
                >
                  <div className="tab-content" id={`tab-${tab.key}`}>
                    <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                      {tab.description}
                    </Paragraph>
                    {tab.content}
                  </div>
                </TabPane>
              ))}
            </Tabs>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="side-card" variant="borderless">
            <Title level={4}>快速导航</Title>
            <Anchor offsetTop={80} onClick={(e, link) => {
              // 避免 HashRouter 误把 "#tab-x" 当作路由，导致空白
              e.preventDefault();
              const href = link?.href || '';
              const match = href.match(/#tab-(\d+)/);
              if (match) {
                const key = match[1];
                setActiveKey(key);
                const el = document.getElementById(`tab-${key}`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }
            }}>
              {tabs.map(tab => (
                <Anchor.Link href={`#tab-${tab.key}`} title={tab.title} key={tab.key} />
              ))}
            </Anchor>
          </Card>
          <Card className="side-card" variant="borderless" style={{ marginTop: 24 }}>
            <Title level={4}>参考标准</Title>
            <Paragraph>
              IEC 60255、IEEE C37系列、GB/T相关标准与企业导则。
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default KnowledgePage;
